import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { NavigationBreadcrumbLevel, NavigationBreadcrumbOption } from '@shared/ui';
import { isLikelyCipherText } from '@telespot/shared/util';
import { Info, NavigationData, SampleListItem } from '@analysis/infra/telespot';
import { CaseNavigationReference, NavigationState, initialNavigationState } from './navigation.state';

/**
 * Pure navigation state for the analysis views: current workspace/case/sample
 * references, prev/next navigation targets, and the breadcrumb dropdown
 * options. Selectors + updaters only — fetching lives in
 * `BreadcrumbNavigationEffects`.
 */
@Injectable()
export class NavigationStore extends ComponentStore<NavigationState> {
  constructor() {
    super(initialNavigationState);
  }

  // Selectors
  public readonly previousCase$ = this.select((state) => state.previousCase);
  public readonly nextCase$ = this.select((state) => state.nextCase);
  public readonly previousSample$ = this.select((state) => state.previousSample);
  public readonly nextSample$ = this.select((state) => state.nextSample);
  public readonly currentCase$ = this.select((state) => state.currentCase);
  public readonly currentSample$ = this.select((state) => state.currentSample);
  public readonly currentWorkspace$ = this.select((state) => state.currentWorkspace);
  public readonly error$ = this.select((state) => state.error);
  public readonly workspaceOptions$ = this.select((state) => state.workspaceOptions);
  public readonly rawCasesByWorkspaceId$ = this.select((state) => state.rawCasesByWorkspaceId);
  public readonly samplesByCaseId$ = this.select((state) => state.samplesByCaseId);
  public readonly decryptedNamesByCaseId$ = this.select((state) => state.decryptedNamesByCaseId);

  /**
   * `currentCase$` with `name` overridden by the decrypted value whenever the
   * auto-decrypt effect has resolved it. Falls back to the raw value (possibly
   * ciphertext) — `breadcrumbLevels$` masks that as `******` until decryption
   * lands.
   */
  public readonly currentCaseDisplay$ = this.select(
    this.currentCase$,
    this.decryptedNamesByCaseId$,
    (currentCase, decrypted) => {
      if (!currentCase) return currentCase ?? null;
      const decryptedName = decrypted[currentCase.id];
      return decryptedName ? { ...currentCase, name: decryptedName } : currentCase;
    }
  );

  /**
   * Case lists with `name` overridden by the decrypted value for each entry the
   * auto-decrypt effect has already resolved. Same fallback behavior as
   * `currentCaseDisplay$`. Cases of a workspace that was never unlocked in this session
   * stay ciphertext and are masked by `breadcrumbLevels$`.
   */
  public readonly casesByWorkspaceId$ = this.select(
    this.rawCasesByWorkspaceId$,
    this.decryptedNamesByCaseId$,
    (byWorkspaceId, decrypted): Record<string, Info[] | null> => {
      if (Object.keys(decrypted).length === 0) return byWorkspaceId;
      const decryptedByWorkspaceId: Record<string, Info[] | null> = {};
      for (const [workspaceId, cases] of Object.entries(byWorkspaceId)) {
        decryptedByWorkspaceId[workspaceId] = cases
          ? cases.map((entry) => (decrypted[entry.id] ? { ...entry, name: decrypted[entry.id] } : entry))
          : cases;
      }
      return decryptedByWorkspaceId;
    }
  );

  /** The open workspace's cases, decryption-aware — what the case level lists. */
  public readonly currentCaseOptions$ = this.select(
    this.currentWorkspace$,
    this.casesByWorkspaceId$,
    (workspace, byWorkspaceId): Info[] | null => (workspace ? byWorkspaceId[workspace.id] ?? null : null)
  );

  /** The open case's samples — what the sample level lists. */
  public readonly currentSampleOptions$ = this.select(
    this.currentCase$,
    this.samplesByCaseId$,
    (currentCase, byCaseId): SampleListItem[] | null => (currentCase ? byCaseId[currentCase.id] ?? null : null)
  );

  /**
   * The three navigation levels in the shape `ts-navigation-breadcrumb` expects, as
   * cascades: a workspace expands into its cases and a case into its samples, so the
   * user can drill down and commit once at the leaf instead of the view guessing a
   * first case and first sample on the way.
   *
   * Options still `null` render as a loading row, so the bar shows the current names
   * without waiting for any fetch. Case names that still look like ciphertext
   * (decryption not landed, or a workspace never unlocked this session) are masked
   * here — the breadcrumb is domain-agnostic and renders names verbatim.
   */
  public readonly breadcrumbLevels$ = this.select(
    this.currentWorkspace$,
    this.currentCaseDisplay$,
    this.currentSample$,
    this.workspaceOptions$,
    this.casesByWorkspaceId$,
    this.samplesByCaseId$,
    (
      workspace,
      currentCase,
      sample,
      workspaceOptions,
      casesByWorkspaceId,
      samplesByCaseId
    ): NavigationBreadcrumbLevel[] => {
      const sampleNodes = (caseId: string): NavigationBreadcrumbOption[] | null => {
        const samples = samplesByCaseId[caseId];
        if (!samples) return null;
        return samples.map((item) => ({
          id: item.id,
          name: item.name,
          levelKey: 'sample',
          badge: item.methodType,
        }));
      };

      const caseNodes = (workspaceId: string): NavigationBreadcrumbOption[] | null => {
        const cases = casesByWorkspaceId[workspaceId];
        if (!cases) return null;
        return cases.map((entry) => ({
          ...NavigationStore.maskCipherName({ id: entry.id, name: entry.name }),
          levelKey: 'case',
          expandable: true,
          children: sampleNodes(entry.id),
          emptyKey: 'navigation.no_samples_in_case',
        }));
      };

      return [
        {
          key: 'workspace',
          labelKey: 'core.workspace',
          icon: 'ri-folder-line',
          current: workspace ?? null,
          options:
            workspaceOptions?.map((entry) => ({
              id: entry.id,
              name: entry.name,
              levelKey: 'workspace',
              expandable: true,
              children: caseNodes(entry.id),
              emptyKey: 'navigation.no_cases_in_workspace',
            })) ?? null,
        },
        {
          key: 'case',
          labelKey: 'core.case',
          icon: 'ri-file-list-2-line',
          current: currentCase ? NavigationStore.maskCipherName(currentCase) : null,
          options: workspace ? caseNodes(workspace.id) : null,
        },
        {
          key: 'sample',
          labelKey: 'core.sample',
          icon: 'ri-test-tube-line',
          current: sample ?? null,
          options: currentCase ? sampleNodes(currentCase.id) : null,
        },
      ];
    }
  );

  private static maskCipherName<T extends { name: string }>(entity: T): T {
    return isLikelyCipherText(entity.name) ? { ...entity, name: '******' } : entity;
  }

  // Updaters
  readonly setError = this.updater((state, error: string | null) => ({
    ...state,
    error,
  }));

  readonly updateNavigationReferences = this.updater((state, navigationData: NavigationData) => ({
    ...state,
    previousCase: navigationData.previousCase,
    nextCase: navigationData.nextCase,
    previousSample: navigationData.previousSample,
    nextSample: navigationData.nextSample,
    currentCase: navigationData.currentCase,
    currentSample: navigationData.currentSample,
    currentWorkspace: navigationData.currentWorkspace,
    error: null,
  }));

  /** `null` = a fetch is pending (the dropdown shows a loading row). */
  readonly setWorkspaceOptions = this.updater((state, workspaceOptions: Info[] | null) => ({
    ...state,
    workspaceOptions,
  }));

  /** `null` cases = a fetch for that workspace is pending. */
  readonly setCasesForWorkspace = this.updater(
    (state, { workspaceId, cases }: { workspaceId: string; cases: Info[] | null }) => ({
      ...state,
      rawCasesByWorkspaceId: { ...state.rawCasesByWorkspaceId, [workspaceId]: cases },
    })
  );

  /** `null` samples = a fetch for that case is pending. */
  readonly setSamplesForCase = this.updater(
    (state, { caseId, samples }: { caseId: string; samples: SampleListItem[] | null }) => ({
      ...state,
      samplesByCaseId: { ...state.samplesByCaseId, [caseId]: samples },
    })
  );

  readonly mergeDecryptedNames = this.updater((state, namesByCaseId: Record<string, string>) => ({
    ...state,
    decryptedNamesByCaseId: { ...state.decryptedNamesByCaseId, ...namesByCaseId },
  }));
}
